import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });
    // Second step when two-factor sign-in is on: the code from the authenticator.
    const [mfaToken, setMfaToken] = useState(null);
    const [code, setCode] = useState("");
    const [remember, setRemember] = useState(true);
    const { login, verify2fa } = useAuth();
    const navigate = useNavigate();

    const welcome = (userData) => {
        toast.success(`Welcome back, ${userData.name}!`);
        navigate(userData.role === "SRM" ? "/dashboard/srm" : "/suggestions");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await login(formData.email, formData.password);
            if (res.mfaRequired) {
                setMfaToken(res.mfaToken);
                setCode("");
            } else {
                welcome(res);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Authentication failed");
        } finally {
            setLoading(false);
        }
    };

    const handleCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            welcome(await verify2fa(formData.email, mfaToken, code.trim(), remember));
        } catch (error) {
            const status = error.response?.status;
            toast.error(error.response?.data?.detail || "That didn't work");
            // Took too long, or locked: back to the password.
            if (status === 429 || (status === 401 && error.response?.data?.detail !== "Wrong code")) setMfaToken(null);
            setCode("");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen">
            {/* Left side - Branding */}
            <div className="hidden w-1/2 flex-col justify-between bg-card p-12 lg:flex">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">
                        BOUTIQAAT
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Celebrity Sales Management
                    </p>
                </div>

                <div className="space-y-6">
                    <div className="max-w-md">
                        <h2 className="font-heading text-4xl font-bold leading-tight tracking-tight">
                            Maximize Celebrity
                            <br />
                            Sales Performance
                        </h2>
                        <p className="mt-4 text-muted-foreground">
                            AI-powered product suggestions, sales analytics, and performance tracking for your celebrity boutiques.
                        </p>
                    </div>
                </div>

                <p className="text-xs text-muted-foreground">
                    &copy; 2026 Jarves. All rights reserved.
                </p>
            </div>

            {/* Right side - Form */}
            <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
                <div className="w-full max-w-md space-y-8">
                    {mfaToken ? (
                        <form onSubmit={handleCode} className="space-y-5">
                            <div className="text-center lg:text-left">
                                <h2 className="flex items-center justify-center gap-2 font-heading text-2xl font-bold lg:justify-start">
                                    <ShieldCheck className="h-6 w-6" strokeWidth={1.5} /> Enter your code
                                </h2>
                                <p className="mt-2 text-sm text-muted-foreground">
                                    Open your authenticator app and type the 6-digit code for Boutiqaat SRM. Lost your phone? Type one of your recovery codes.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="code">Code</Label>
                                <Input
                                    id="code"
                                    autoFocus
                                    autoComplete="one-time-code"
                                    inputMode="text"
                                    placeholder="123 456"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.slice(0, 20))}
                                    className="h-11 bg-card text-center font-mono text-lg tracking-widest"
                                    required
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} />
                                Remember this device for 30 days
                            </label>
                            <Button type="submit" className="w-full gap-2" disabled={loading || code.trim().length < 6}>
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify <ArrowRight className="h-4 w-4" /></>}
                            </Button>
                            <button type="button" className="w-full text-center text-sm text-muted-foreground hover:text-foreground" onClick={() => setMfaToken(null)}>
                                Back
                            </button>
                        </form>
                    ) : (
                    <>
                    <div className="text-center lg:text-left">
                        <h2 className="font-heading text-2xl font-bold">
                            Sign in to your account
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Enter your credentials to access the dashboard
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                data-testid="email-input"
                                placeholder="name@boutiqaat.com"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData({ ...formData, email: e.target.value })
                                }
                                className="h-11 bg-card"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                data-testid="password-input"
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={(e) =>
                                    setFormData({ ...formData, password: e.target.value })
                                }
                                className="h-11 bg-card"
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            data-testid="submit-btn"
                            className="w-full gap-2"
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    Sign In
                                    <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </Button>
                    </form>

                    {/* Accounts are created by the SRM under Manage Users; there is no sign-up. */}
                    <p className="text-center text-sm text-muted-foreground">
                        No account? Ask your SRM to create one for you.
                    </p>
                    </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;
